<template>
  <v-container fluid>
    <v-card class="pa-3 mb-3">
      <v-layout row wrap align-center>
        <v-flex xs12 sm8 class="px-2">
          <v-text-field v-model="search" :label='$t("businessPage.searchLabel")' append-icon="search"
            :loading="loading" @keyup.enter="fetch" @click:append="fetch" />
        </v-flex>
        <v-flex xs12 sm4 class="px-2 text-xs-end">
          <v-btn v-if="manage" color="primary" @click="openCreate">{{ $t("businessPage.addBusiness") }}</v-btn>
        </v-flex>
      </v-layout>
    </v-card>

    <v-card class="pa-3 mb-3" v-if="pendingRequests.length">
      <div class="title mb-2">{{ $t("businessPage.pendingRechargeRequests") }}</div>
      <v-data-table :headers="reqHeaders" :items="pendingRequests" hide-actions class="elevation-0">
        <template slot="items" slot-scope="props">
          <td>{{ props.item.business_account && props.item.business_account.name }}</td>
          <td><strong>{{ formatNum(props.item.amount) }} {{ $t("businessPage.syp") }}</strong></td>
          <td>{{ props.item.note || '—' }}</td>
          <td>{{ formatDate(props.item.created_at) }}</td>
          <td>
            <v-btn small flat color="green" :loading="saving" @click="approveRequest(props.item)">{{ $t("businessPage.approve") }}</v-btn>
            <v-btn small flat color="red" @click="rejectRequest(props.item)">{{ $t("businessPage.reject") }}</v-btn>
          </td>
        </template>
      </v-data-table>
    </v-card>

    <v-card>
      <v-data-table :headers="headers" :items="accounts" :loading="loading" :total-items="total"
        :pagination.sync="pagination" class="elevation-0">
        <template slot="items" slot-scope="props">
          <td>{{ props.item.id }}</td>
          <td>{{ props.item.name }}</td>
          <td>{{ props.item.account_code || '-' }}</td>
          <td>{{ typeLabel(props.item.type) }}</td>
          <td><strong>{{ formatNum(props.item.balance) }} {{ $t("businessPage.syp") }}</strong></td>
          <td>{{ props.item.max_per_order == null ? '—' : formatNum(props.item.max_per_order) }}</td>
          <td>{{ props.item.daily_limit == null ? '—' : formatNum(props.item.daily_limit) }}</td>
          <td>
            <v-chip v-if="props.item.active" color="green" text-color="white" small>{{ $t("businessPage.active") }}</v-chip>
            <v-chip v-else color="red" text-color="white" small>{{ $t("businessPage.disabled") }}</v-chip>
            <v-chip v-if="props.item.status && props.item.status !== 'APPROVED'"
              :color="props.item.status === 'PENDING' ? 'orange' : 'grey'" text-color="white" small>
              {{ statusLabel(props.item.status) }}
            </v-chip>
          </td>
          <td>
            <v-btn small flat color="green" @click="openRecharge(props.item)">{{ $t("businessPage.recharge") }}</v-btn>
            <v-btn v-if="manage" small flat color="primary" @click="openEdit(props.item)">{{ $t("businessPage.edit") }}</v-btn>
            <v-btn small flat @click="openTransactions(props.item)">{{ $t("businessPage.log") }}</v-btn>
            <v-btn small flat color="blue" @click="openDocuments(props.item)">{{ $t("businessPage.docs") }}</v-btn>
            <v-btn small flat :color="props.item.active ? 'red' : 'green'" @click="toggleActive(props.item)">
              {{ props.item.active ? $t("businessPage.disable") : $t("businessPage.enable") }}
            </v-btn>
          </td>
        </template>
        <template slot="no-data">
          <div class="text-xs-center pa-4 grey--text">{{ $t("businessPage.noAccounts") }}</div>
        </template>
      </v-data-table>
    </v-card>

    <!-- Create / edit -->
    <v-dialog v-model="formDialog" max-width="480">
      <v-card class="pa-3">
        <div class="title mb-2">{{ editing ? $t("businessPage.editBusiness") : $t("businessPage.addBusiness") }}</div>
        <v-text-field v-model="form.name" :label='$t("businessPage.name")' />
        <v-select v-model="form.type" :items="typeOptions" :label='$t("businessPage.type")' />
        <v-text-field v-model="form.phone" :label='$t("businessPage.phoneNumber")' />
        <v-text-field v-model="form.email" :label='$t("businessPage.email")' />
        <v-text-field v-model="form.address" :label='$t("businessPage.address")' />
        <v-text-field v-model.number="form.max_per_order" type="number" :label='$t("businessPage.maxPerOrderLabel")' />
        <v-text-field v-model.number="form.daily_limit" type="number" :label='$t("businessPage.dailyLimitLabel")' />
        <v-select v-if="editing" v-model="form.status" :items="statusOptions" :label='$t("businessPage.status")' />
        <v-switch v-model="form.add_employees_enabled" :label='$t("businessPage.allowAddEmployees")' color="primary" hide-details />
        <v-switch v-model="form.guest_form_enabled" :label='$t("businessPage.allowGuestForm")' color="primary" hide-details />
        <div class="text-xs-end mt-2">
          <v-btn flat @click="formDialog = false">{{ $t("businessPage.cancel") }}</v-btn>
          <v-btn color="primary" :loading="saving" @click="save">{{ $t("businessPage.save") }}</v-btn>
        </div>
      </v-card>
    </v-dialog>

    <!-- Recharge -->
    <v-dialog v-model="rechargeDialog" max-width="420">
      <v-card class="pa-3" v-if="rechargeAccount">
        <div class="title mb-2">{{ $t("businessPage.rechargeTitle", { name: rechargeAccount.name }) }}</div>
        <div class="grey--text mb-2">{{ $t("businessPage.balance") }}: {{ formatNum(rechargeAccount.balance) }} {{ $t("businessPage.syp") }}</div>
        <v-text-field v-model.number="rechargeAmount" type="number" :label='$t("businessPage.amountToAdd")' />
        <v-text-field v-model="rechargeNote" :label='$t("businessPage.noteHint")' />
        <div class="text-xs-end">
          <v-btn flat @click="rechargeDialog = false">{{ $t("businessPage.cancel") }}</v-btn>
          <v-btn color="green" dark :loading="saving" @click="doRecharge">{{ $t("businessPage.addBalance") }}</v-btn>
        </div>
      </v-card>
    </v-dialog>

    <!-- Transactions -->
    <v-dialog v-model="txDialog" max-width="700">
      <v-card class="pa-3" v-if="txAccount">
        <div class="title mb-2">{{ $t("businessPage.transactionsTitle", { name: txAccount.name }) }}</div>
        <v-data-table :headers="txHeaders" :items="transactions" :loading="txLoading" hide-actions class="elevation-0">
          <template slot="items" slot-scope="props">
            <td>{{ formatDate(props.item.created_at) }}</td>
            <td>{{ txTypeLabel(props.item.type) }}</td>
            <td :style="{ color: props.item.type === 'DEBIT_BOOKING' ? 'red' : 'green' }">
              {{ props.item.type === 'DEBIT_BOOKING' ? '-' : '+' }}{{ formatNum(props.item.amount) }}
            </td>
            <td>{{ formatNum(props.item.balance_after) }}</td>
            <td>{{ props.item.note || props.item.performed_by_username || '—' }}</td>
          </template>
          <template slot="no-data"><div class="text-xs-center pa-3 grey--text">{{ $t("businessPage.noTransactions") }}</div></template>
        </v-data-table>
      </v-card>
    </v-dialog>

    <!-- Documents / attachments (admin only) -->
    <v-dialog v-model="docsDialog" max-width="640">
      <v-card class="pa-3" v-if="docsAccount">
        <div class="title mb-2">{{ $t("businessPage.documentsTitle", { name: docsAccount.name }) }}</div>
        <v-data-table :headers="docHeaders" :items="documents" :loading="docsLoading" hide-actions class="elevation-0">
          <template slot="items" slot-scope="props">
            <td>{{ props.item.name }}</td>
            <td>{{ humanSize(props.item.size) }}</td>
            <td>{{ formatDate(props.item.uploaded_at) }}</td>
            <td>
              <v-btn small flat color="primary" :loading="docActing === props.item.id" @click="viewDocument(props.item)">{{ $t("businessPage.view") }}</v-btn>
              <v-btn small flat @click="downloadDocument(props.item)">{{ $t("businessPage.download") }}</v-btn>
              <v-btn small flat color="red" @click="deleteDocument(props.item)">{{ $t("businessPage.delete") }}</v-btn>
            </td>
          </template>
          <template slot="no-data"><div class="text-xs-center pa-3 grey--text">{{ $t("businessPage.noDocuments") }}</div></template>
        </v-data-table>
        <v-divider class="my-3" />
        <div class="subheading mb-2">{{ $t("businessPage.addDocument") }}</div>
        <input type="file" ref="docInput" style="display:none" @change="onDocPicked" />
        <v-layout row wrap align-center>
          <v-flex xs12 sm5 class="px-1">
            <v-text-field v-model="docName" :label='$t("businessPage.titleOptional")' hide-details />
          </v-flex>
          <v-flex xs12 sm4 class="px-1">
            <v-btn flat block @click="$refs.docInput.click()">{{ selectedDocFile ? selectedDocFile.name : $t("businessPage.chooseFile") }}</v-btn>
          </v-flex>
          <v-flex xs12 sm3 class="px-1 text-xs-end">
            <v-btn color="green" dark :loading="uploading" :disabled="!selectedDocFile" @click="uploadDocument">{{ $t("businessPage.upload") }}</v-btn>
          </v-flex>
        </v-layout>
        <div class="grey--text caption mt-1">{{ $t("businessPage.docHint") }}</div>
        <div class="text-xs-end mt-2"><v-btn flat @click="docsDialog = false">{{ $t("businessPage.close") }}</v-btn></div>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import { formatCompanyTime } from "@/util/companyTime";

export default {
  name: "BusinessAccounts",
  title: "businessPage.pageTitle",
  // manage=true (the /business "Businesses" tab) shows Add + Edit. On the
  // /business-accounts (Balances) page it is false -> view + recharge + docs + log.
  props: {
    manage: { type: Boolean, default: false },
  },
  data() {
    return {
      search: "",
      loading: false,
      accounts: [],
      total: 0,
      pagination: { rowsPerPage: 10, page: 1 },
      formDialog: false,
      editing: null,
      saving: false,
      form: { name: "", type: "hotel", phone: "", email: "", address: "", max_per_order: null, daily_limit: null, status: "APPROVED", add_employees_enabled: true, guest_form_enabled: false },
      rechargeDialog: false,
      rechargeAccount: null,
      rechargeAmount: null,
      rechargeNote: "",
      txDialog: false,
      txAccount: null,
      transactions: [],
      txLoading: false,
      pendingRequests: [],
      docsDialog: false,
      docsAccount: null,
      documents: [],
      docsLoading: false,
      selectedDocFile: null,
      docName: "",
      uploading: false,
      docActing: null,
    };
  },
  computed: {
    typeOptions() {
      return [
        { text: this.$t("businessPage.typeHotel"), value: "hotel" },
        { text: this.$t("businessPage.typeHospital"), value: "hospital" },
        { text: this.$t("businessPage.typeCompany"), value: "company" },
        { text: this.$t("businessPage.typeRestaurant"), value: "restaurant" },
        { text: this.$t("businessPage.typeOffice"), value: "office" },
        { text: this.$t("businessPage.typeOther"), value: "other" },
      ];
    },
    statusOptions() {
      return [
        { text: this.$t("businessPage.statusPending"), value: "PENDING" },
        { text: this.$t("businessPage.statusApproved"), value: "APPROVED" },
        { text: this.$t("businessPage.statusDisabled"), value: "DISABLED" },
      ];
    },
    reqHeaders() {
      return [
        { text: this.$t("businessPage.colBusiness"), value: "business", sortable: false },
        { text: this.$t("businessPage.colAmount"), value: "amount", sortable: false },
        { text: this.$t("businessPage.colNote"), value: "note", sortable: false },
        { text: this.$t("businessPage.colDate"), value: "created_at", sortable: false },
        { text: "", value: "actions", sortable: false },
      ];
    },
    headers() {
      return [
        { text: "#", value: "id", sortable: false },
        { text: this.$t("businessPage.colName"), value: "name", sortable: false },
        { text: this.$t("businessPage.colAccountCode"), value: "account_code", sortable: false },
        { text: this.$t("businessPage.colType"), value: "type", sortable: false },
        { text: this.$t("businessPage.colBalance"), value: "balance", sortable: false },
        { text: this.$t("businessPage.colMaxPerOrder"), value: "max_per_order", sortable: false },
        { text: this.$t("businessPage.colDaily"), value: "daily_limit", sortable: false },
        { text: this.$t("businessPage.colStatus"), value: "active", sortable: false },
        { text: "", value: "actions", sortable: false },
      ];
    },
    txHeaders() {
      return [
        { text: this.$t("businessPage.colDate"), value: "created_at", sortable: false },
        { text: this.$t("businessPage.colType"), value: "type", sortable: false },
        { text: this.$t("businessPage.colAmount"), value: "amount", sortable: false },
        { text: this.$t("businessPage.colBalance"), value: "balance_after", sortable: false },
        { text: this.$t("businessPage.colNote"), value: "note", sortable: false },
      ];
    },
    docHeaders() {
      return [
        { text: this.$t("businessPage.colName"), value: "name", sortable: false },
        { text: this.$t("businessPage.colSize"), value: "size", sortable: false },
        { text: this.$t("businessPage.colDate"), value: "uploaded_at", sortable: false },
        { text: "", value: "actions", sortable: false },
      ];
    },
  },
  watch: {
    pagination: { handler() { this.fetch(); }, deep: true },
  },
  mounted() { this.fetch(); this.loadPendingRequests(); },
  methods: {
    formatNum(v) { return v == null ? "—" : this.formatMoney(v); },
    // Final-version doc 2026-10-05: company clock (UTC+3), not the PC's zone.
    formatDate(d) { return formatCompanyTime(d, "YYYY-MM-DD HH:mm:ss", "—"); },
    typeLabel(t) {
      if (!t) return t;
      const key = "businessPage.type" + t.charAt(0).toUpperCase() + t.slice(1).toLowerCase();
      return this.$te(key) ? this.$t(key) : t;
    },
    statusLabel(s) {
      const map = { PENDING: "statusPending", APPROVED: "statusApproved", DISABLED: "statusDisabled" };
      return map[s] ? this.$t("businessPage." + map[s]) : s;
    },
    txTypeLabel(t) {
      const map = { CREDIT_RECHARGE: "txRecharge", DEBIT_BOOKING: "txDebitBooking", ADJUSTMENT: "txAdjustment" };
      return map[t] ? this.$t("businessPage." + map[t]) : t;
    },
    loadPendingRequests() {
      this.axios.get("business-recharge-requests", { params: { status: "PENDING", perPage: 100 } })
        .then(({ data }) => { this.pendingRequests = (data.data && data.data.data) || []; })
        .catch(() => { this.pendingRequests = []; });
    },
    approveRequest(req) {
      this.saving = true;
      this.axios.post(`business-recharge-requests/${req.id}/approve`)
        .then(() => {
          window.getApp.$emit("SHOW_TOAST", this.$t("businessPage.toastApproved"), "green");
          this.loadPendingRequests();
          this.fetch();
        }).catch((e) => {
          window.getApp.$emit("SHOW_TOAST", (e.response && e.response.data && e.response.data.message) || this.$t("businessPage.toastFailed"), "red");
        }).finally(() => (this.saving = false));
    },
    rejectRequest(req) {
      this.axios.post(`business-recharge-requests/${req.id}/reject`)
        .then(() => {
          window.getApp.$emit("SHOW_TOAST", this.$t("businessPage.toastRejected"), "green");
          this.loadPendingRequests();
        }).catch((e) => {
          window.getApp.$emit("SHOW_TOAST", (e.response && e.response.data && e.response.data.message) || this.$t("businessPage.toastFailed"), "red");
        });
    },
    fetch() {
      this.loading = true;
      this.axios.get("business-account", {
        params: { search: this.search, perPage: this.pagination.rowsPerPage, page: this.pagination.page },
      }).then(({ data }) => {
        const p = data.data || {};
        this.accounts = p.data || [];
        this.total = p.total || this.accounts.length;
      }).catch(() => {
        window.getApp.$emit("SHOW_TOAST", this.$t("businessPage.toastFailedLoad"), "red");
      }).finally(() => (this.loading = false));
    },
    openCreate() {
      this.editing = null;
      this.form = { name: "", type: "hotel", phone: "", email: "", address: "", max_per_order: null, daily_limit: null, status: "APPROVED", add_employees_enabled: true, guest_form_enabled: false };
      this.formDialog = true;
    },
    openEdit(account) {
      this.editing = account;
      this.form = {
        name: account.name,
        type: account.type,
        phone: account.phone || "",
        email: account.email || "",
        address: account.address || "",
        max_per_order: account.max_per_order == null ? null : account.max_per_order / 100,
        daily_limit: account.daily_limit == null ? null : account.daily_limit / 100,
        status: account.status || "APPROVED",
        add_employees_enabled: account.add_employees_enabled !== false,
        guest_form_enabled: !!account.guest_form_enabled,
      };
      this.formDialog = true;
    },
    save() {
      if (!this.form.name) { window.getApp.$emit("SHOW_TOAST", this.$t("businessPage.toastNameRequired"), "red"); return; }
      this.saving = true;
      const payload = {
        name: this.form.name,
        type: this.form.type,
        phone: this.form.phone || null,
        email: this.form.email || null,
        address: this.form.address || null,
        max_per_order: this.form.max_per_order === "" || this.form.max_per_order == null ? null : this.toStoredAmount(this.form.max_per_order),
        daily_limit: this.form.daily_limit === "" || this.form.daily_limit == null ? null : this.toStoredAmount(this.form.daily_limit),
        add_employees_enabled: this.form.add_employees_enabled,
        guest_form_enabled: this.form.guest_form_enabled,
        // status only applies when editing an existing establishment
        ...(this.editing ? { status: this.form.status } : {}),
      };
      const req = this.editing
        ? this.axios.put(`business-account/${this.editing.id}`, payload)
        : this.axios.post("business-account", payload);
      req.then(() => {
        window.getApp.$emit("SHOW_TOAST", this.$t("businessPage.toastSaved"), "green");
        this.formDialog = false;
        this.fetch();
      }).catch((e) => {
        window.getApp.$emit("SHOW_TOAST", (e.response && e.response.data && e.response.data.message) || this.$t("businessPage.toastFailed"), "red");
      }).finally(() => (this.saving = false));
    },
    toggleActive(account) {
      this.axios.put(`business-account/${account.id}`, { active: !account.active })
        .then(() => { account.active = !account.active; })
        .catch((e) => {
          window.getApp.$emit("SHOW_TOAST", (e.response && e.response.data && e.response.data.message) || this.$t("businessPage.toastFailed"), "red");
        });
    },
    openRecharge(account) {
      this.rechargeAccount = account;
      this.rechargeAmount = null;
      this.rechargeNote = "";
      this.rechargeDialog = true;
    },
    doRecharge() {
      if (!this.rechargeAmount || this.rechargeAmount <= 0) { window.getApp.$emit("SHOW_TOAST", this.$t("businessPage.toastEnterAmount"), "red"); return; }
      this.saving = true;
      this.axios.post(`business-account/${this.rechargeAccount.id}/recharge`, { amount: this.toStoredAmount(this.rechargeAmount), note: this.rechargeNote })
        .then(() => {
          window.getApp.$emit("SHOW_TOAST", this.$t("businessPage.toastBalanceAdded"), "green");
          this.rechargeDialog = false;
          this.fetch();
        }).catch((e) => {
          window.getApp.$emit("SHOW_TOAST", (e.response && e.response.data && e.response.data.message) || this.$t("businessPage.toastFailed"), "red");
        }).finally(() => (this.saving = false));
    },
    openTransactions(account) {
      this.txAccount = account;
      this.transactions = [];
      this.txDialog = true;
      this.txLoading = true;
      this.axios.get(`business-account/${account.id}/transactions`, { params: { perPage: 50 } })
        .then(({ data }) => { this.transactions = (data.data && data.data.data) || []; })
        .catch(() => {})
        .finally(() => (this.txLoading = false));
    },
    humanSize(b) {
      if (b == null) return "—";
      const u = ["B", "KB", "MB", "GB"];
      let i = 0, n = Number(b);
      while (n >= 1024 && i < u.length - 1) { n /= 1024; i++; }
      return n.toFixed(n < 10 && i > 0 ? 1 : 0) + " " + u[i];
    },
    openDocuments(account) {
      this.docsAccount = account;
      this.documents = [];
      this.selectedDocFile = null;
      this.docName = "";
      this.docsDialog = true;
      this.docsLoading = true;
      this.axios.get(`business-account/${account.id}/documents`)
        .then(({ data }) => { this.documents = data.data || []; })
        .catch(() => {})
        .finally(() => (this.docsLoading = false));
    },
    onDocPicked(e) {
      this.selectedDocFile = (e.target.files && e.target.files[0]) || null;
    },
    uploadDocument() {
      if (!this.selectedDocFile) return;
      this.uploading = true;
      const fd = new FormData();
      fd.append("document", this.selectedDocFile);
      if (this.docName) fd.append("name", this.docName);
      this.axios.post(`business-account/${this.docsAccount.id}/documents`, fd, { headers: { "content-type": "multipart/form-data" } })
        .then(({ data }) => {
          this.documents = data.data || [];
          this.selectedDocFile = null;
          this.docName = "";
          if (this.$refs.docInput) this.$refs.docInput.value = "";
          window.getApp.$emit("SHOW_TOAST", this.$t("businessPage.toastUploaded"), "green");
        })
        .catch((e) => {
          window.getApp.$emit("SHOW_TOAST", (e.response && e.response.data && e.response.data.message) || this.$t("businessPage.toastUploadFailed"), "red");
        })
        .finally(() => (this.uploading = false));
    },
    fetchDocBlob(doc) {
      return this.axios.get(`business-account/${this.docsAccount.id}/documents/${doc.id}/download`, { responseType: "blob" });
    },
    viewDocument(doc) {
      this.docActing = doc.id;
      this.fetchDocBlob(doc)
        .then(({ data }) => { window.open(URL.createObjectURL(data), "_blank"); })
        .catch(() => window.getApp.$emit("SHOW_TOAST", this.$t("businessPage.toastFailedOpen"), "red"))
        .finally(() => (this.docActing = null));
    },
    downloadDocument(doc) {
      this.fetchDocBlob(doc)
        .then(({ data }) => {
          const url = URL.createObjectURL(data);
          const a = document.createElement("a");
          a.href = url;
          a.download = doc.original_name || doc.name || "document";
          document.body.appendChild(a);
          a.click();
          a.remove();
          URL.revokeObjectURL(url);
        })
        .catch(() => window.getApp.$emit("SHOW_TOAST", this.$t("businessPage.toastFailedDownload"), "red"));
    },
    deleteDocument(doc) {
      this.axios.delete(`business-account/${this.docsAccount.id}/documents/${doc.id}`)
        .then(({ data }) => {
          this.documents = data.data || [];
          window.getApp.$emit("SHOW_TOAST", this.$t("businessPage.toastDeleted"), "green");
        })
        .catch(() => window.getApp.$emit("SHOW_TOAST", this.$t("businessPage.toastFailed"), "red"));
    },
  },
};
</script>
